import { CopyOutlined } from '@ant-design/icons';
import { Input, message, Modal } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import { useGetOrCreateYookassaPaymentMutation } from '@app/api/ClientPayments';
import { DATE_FORMATS, PNG } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

import type { YooKassaPaymentProps } from '../types';

export const YooKassaPayment: React.FC<YooKassaPaymentProps> = ({
  isModalOpen,
  clientId,
  recordId,
  paymentAmount,
  paymentDate,
  paymentComment,
  setPaymentDate,
  setPaymentComment,
  setIsOpen,
  fetchPayments,
  handledYooKassaPaymentId,
  receiptId,
  yooKassaPaymentMethodCode,
  isNeedToCreateYooKassaPayment,
}) => {
  const [yooKassaPaymentLink, setYooKassaPaymentLink] = useState<string>('');
  const [yooKassaPaymentQR, setYooKassaPaymentQR] = useState<string>('');

  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [getOrCreateYookassaPayment] = useGetOrCreateYookassaPaymentMutation();

  useEffect(() => {
    const fetchYooKassaPaymentLink = async () => {
      try {
        if (
          !isModalOpen ||
          (!isNeedToCreateYooKassaPayment && !handledYooKassaPaymentId)
        )
          return;

        const { PaymentUrlQR, ConfirmationUrl } =
          await getOrCreateYookassaPayment({
            paymentAccountId: Number(receiptId),
            clientId,
            recordId,
            amount: parseFloat(paymentAmount.replace(/\s/g, '')),
            paymentDate: dayjs(paymentDate).format(
              DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
            ),
            comment: paymentComment,
            ActiveBranchId,
            clientPaymentsId: handledYooKassaPaymentId,
            paymentMethod: yooKassaPaymentMethodCode,
          }).unwrap();

        !handledYooKassaPaymentId && (await fetchPayments());

        setYooKassaPaymentLink(ConfirmationUrl ?? '');
        setYooKassaPaymentQR(PaymentUrlQR ?? '');
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    fetchYooKassaPaymentLink();
  }, [
    ActiveBranchId,
    clientId,
    fetchPayments,
    getOrCreateYookassaPayment,
    handledYooKassaPaymentId,
    isModalOpen,
    isNeedToCreateYooKassaPayment,
    paymentAmount,
    paymentComment,
    paymentDate,
    receiptId,
    recordId,
    showNotification,
    yooKassaPaymentMethodCode,
  ]);

  useEffect(() => {
    if (!isModalOpen) {
      setYooKassaPaymentLink('');
      setYooKassaPaymentQR('');
    }
  }, [isModalOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(yooKassaPaymentLink);
      message.success('Скопировано!');
    } catch (err) {
      message.error('Не удалось скопировать');
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setPaymentDate(paymentDate);
    setPaymentComment('');
  };

  return (
    <Modal
      footer={null}
      open={isModalOpen}
      onCancel={handleCloseModal}
      title="Оплата через ЮKassa"
    >
      <div className="content-container">
        <h4 data-testid="comment-title">Ссылка на оплату</h4>
        <Input
          value={yooKassaPaymentLink}
          readOnly
          onClick={(e) => e.currentTarget.select()}
          suffix={
            <CopyOutlined onClick={handleCopy} style={{ cursor: 'pointer' }} />
          }
        />
        {yooKassaPaymentQR && (
          <>
            <h4 data-testid="comment-title">QR-код на оплату записи</h4>
            <img
              src={`data:${PNG};base64,${yooKassaPaymentQR}`}
              style={{ width: '264px', alignSelf: 'center' }}
              alt="qr-code"
            />
          </>
        )}
      </div>
    </Modal>
  );
};
